import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";

import { createTheme } from "@mui/material";
import type {} from "@mui/x-date-pickers/themeAugmentation";

import { THEME_NAME } from "./env";

const sharedComponents = {
  MuiInputBase: {
    defaultProps: {
      // These should boots the performance of the TextField components as GlobalStyles for the auto-fill keyframes will not be injected/removed on mount/unmount.
      // This option is intended to help with boosting the initial rendering performance if you are loading a big amount of Input components at once.
      disableInjectingGlobalStyles: true,
    },
  },
  MuiPickersDay: {
    styleOverrides: {
      root: {
        "&.Mui-selected": { color: "#fff" },
      },
    },
  },
} as const;

const sharedTypography = {
  fontFamily: "Roboto",
  h2: {
    fontWeight: 400,
    fontSize: "2.7rem",
  },
  h5: {
    fontSize: "1.25rem",
    lineHeight: "1.5rem",
    fontWeight: 500,
  },
  h6: {
    fontWeight: 400,
    fontSize: "17px",
  },
  caption: {
    color: "#414141",
    fontWeight: 400,
  },
  body1: {
    fontSize: "14px",
    lineHeight: "24px",
    fontWeight: 400,
  },
  body2: {
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 600,
  },
  subtitle1: {
    fontSize: "16px",
    lineHeight: "24px",
    fontWeight: 400,
  },
  button: {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 500,
  },
} as const;

const allianzTheme = createTheme({
  components: sharedComponents,
  typography: sharedTypography,
  shape: { borderRadius: 2 },
  palette: {
    mode: "light",
    primary: {
      main: "#003781",
      contrastText: "#414141",
    },
    secondary: {
      main: "#DFEFF2",
      dark: "#B5DAE6",
    },
    info: {
      light: "#EDE7F6",
      main: "#673AB7",
      contrastText: "#000000",
    },
    success: {
      light: "#00C85333",
      main: "#00C853",
    },
    warning: {
      light: "#ffeb3b80",
      main: "#ffeb3b",
    },
    text: {
      primary: "#2C3E50",
      secondary: "#000000",
    },
  },
});

const defaultTheme = createTheme({
  components: sharedComponents,
  typography: sharedTypography,
  shape: { borderRadius: 2 },
  palette: {
    mode: "light",
    primary: {
      main: "#313849",
      contrastText: "#38BDF8",
    },
    secondary: {
      main: "#E7EEFE",
      dark: "#B5DAE6",
      contrastText: "#FFFFFF",
    },
    info: {
      light: "#EDE7F6",
      main: "#EDE7F6",
    },
    success: {
      light: "#00C85333",
      main: "#00C853",
    },
    warning: {
      light: "#ffeb3b80",
      main: "#ffeb3b",
    },
    text: {
      primary: "#313849",
      secondary: "#313849",
    },
  },
});

const themes: Record<string, ReturnType<typeof createTheme>> = {
  allianz: allianzTheme,
  default: defaultTheme,
};

export const theme = themes[THEME_NAME] ?? defaultTheme;
